import {
  createContext,
  useContext,
  ReactNode,
  useEffect,
  useState,
} from "react";
import {
  BankConnectionMetaImportProgressEnum,
  buildBankConnectionsApiWithToken,
} from "@rentablo/api/apiv3";
import { TppWebFormMultibankingApiProviderEnum } from "@rentablo/api/apiv1";
import { useSearchParams } from "react-router";
import { isAxiosError } from "axios";
import { BankStatus } from "@providers/BankImportStatusProvider/BankStatus.tsx";
import { useFeatureFlag } from "@hooks/useFeatureFlag";
import { ImportedAccount, loadImportedAccounts } from "./loadImportedAccounts";
import {
  BankImportFlow,
  clearPendingBankImport,
  parseBankImportReturn,
  readPendingBankImport,
  stripBankImportReturn,
  writePendingBankImport,
} from "./pendingBankImport";

const POLL_INTERVAL_MS = 1500;
const MAX_RETRY_DELAY_MS = 30_000;

type BankImportStatusContextType = {
  status?: BankConnectionMetaImportProgressEnum | null;
  accounts?: ImportedAccount[];
  closeMessage: () => void;
  type: BankImportFlow | undefined;
};

const BankImportStatusContext = createContext<
  BankImportStatusContextType | undefined
>(undefined);

export const BankImportStatusProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  // The account review reads the same return parameters, and clears them.
  const isReturnReviewed = useFeatureFlag("ACCOUNT_REVIEW");
  const [pending, setPending] = useState(() => readPendingBankImport());
  const [accounts, setAccounts] = useState<ImportedAccount[]>();
  const [status, setStatus] =
    useState<BankConnectionMetaImportProgressEnum | null>();

  const closeMessage = () => {
    clearPendingBankImport();
    setPending(null);
    setStatus(null);
    setAccounts([]);
  };

  useEffect(() => {
    if (isReturnReviewed) return;

    const returned = parseBankImportReturn(searchParams);
    if (!returned) return;

    writePendingBankImport(returned);
    // eslint-disable-next-line react-hooks/set-state-in-effect -- sync from URL params
    setPending(returned);
    setSearchParams(stripBankImportReturn, { replace: true });
  }, [searchParams, setSearchParams, isReturnReviewed]);

  useEffect(() => {
    if (!pending) return;

    let cancelled = false;
    let failedReads = 0;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const showImported = async (bankConnectionIds: number[]) => {
      const imported = await loadImportedAccounts(
        bankConnectionIds,
        pending.provider
      );
      if (!cancelled) setAccounts(imported);
    };

    /** Whether to read again soon, later, or not at all. */
    const tick = async (): Promise<"soon" | "later" | "done"> => {
      // finAPI has finished importing before the web form sends the user back.
      if (pending.provider === TppWebFormMultibankingApiProviderEnum.FIN_API) {
        await showImported(pending.bankConnectionIds);
        if (!cancelled) setStatus(BankConnectionMetaImportProgressEnum.SUCCESS);
        return "done";
      }

      const api = await buildBankConnectionsApiWithToken();
      const { data } = await api.getBankConnection({
        id: pending.bankConnectionIds[0],
      });
      if (cancelled) return "done";

      const progress = data.data.meta.import_progress;
      if (!progress) return "later";

      // Success is only shown with what was imported.
      if (progress === BankConnectionMetaImportProgressEnum.SUCCESS) {
        await showImported([Number(data.data.id)]);
        if (!cancelled) setStatus(progress);
        return "done";
      }

      setStatus(progress);
      return progress === BankConnectionMetaImportProgressEnum.FAILED_WITH_ERROR
        ? "done"
        : "soon";
    };

    // A failed read, or a connection reporting no progress, says nothing
    // about the import, and a lapsed session recovers once the user signs in
    // again, so keep asking, less often.
    const askLater = () => {
      const delay = POLL_INTERVAL_MS * 2 ** failedReads;
      failedReads += 1;
      timeoutId = setTimeout(watch, Math.min(delay, MAX_RETRY_DELAY_MS));
    };

    const watch = () =>
      tick().then(
        (next) => {
          if (cancelled) return;
          // Forgotten so a reload does not announce it again; `pending` stays,
          // as the banner still shows which flow it was.
          if (next === "done") return clearPendingBankImport();
          if (next === "later") return askLater();
          failedReads = 0;
          timeoutId = setTimeout(watch, POLL_INTERVAL_MS);
        },
        (error) => {
          if (cancelled) return;

          // The connection is gone or not this user's: nothing left to watch.
          const httpStatus = isAxiosError(error) ? error.response?.status : 0;
          if (httpStatus === 403 || httpStatus === 404) {
            clearPendingBankImport();
            setPending(null);
            setStatus(null);
            return;
          }
          askLater();
        }
      );

    watch();

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [pending]);

  return (
    <BankImportStatusContext.Provider
      value={{
        status,
        type: pending?.flow,
        accounts,
        closeMessage,
      }}
    >
      {children}
      {status && <BankStatus />}
    </BankImportStatusContext.Provider>
  );
};

export const useBankImportContext = (): BankImportStatusContextType => {
  const context = useContext(BankImportStatusContext);
  if (!context) {
    throw new Error(
      "useBankImportStatus must be used within a BankImportStatusProvider"
    );
  }
  return context;
};
